import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import RichTextBlockEleve from './RichTextBlockEleve';
import ChoixRenderer from './ChoixRenderer';

const LETTRES = ['A', 'B', 'C', 'D'];

const SessionPreviewModal = ({ session, onClose }) => {
  const questions = [...(session.questions || [])].sort((a, b) => a.ordre - b.ordre);
  const [index, setIndex] = useState(0);
  const total = questions.length;

  if (total === 0) return null;

  const q = questions[index];
  const bq = q.banque_question;
  const choix = bq?.contenu?.choix || [];
  const bonneReponse = bq?.contenu?.bonne_reponse || '';
  const bonneIndex = LETTRES.indexOf(bonneReponse);
  const elements = bq?.elements_additionnels?.elements || [];
  const hasElement = choix.some(c => typeof c === 'object' && c !== null);

  return createPortal(
    <div
      className="fixed inset-0 z-[110] bg-slate-950/95 backdrop-blur-md flex flex-col items-center justify-center overflow-auto p-4 sm:p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-2xl bg-slate-800 rounded-2xl flex flex-col max-h-[calc(100vh-3rem)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-white">Aperçu élève</h3>
            <span className="text-xs text-slate-400">{session.titre}</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* Navigation questions */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/50 flex-shrink-0">
          <button
            onClick={() => setIndex(prev => Math.max(0, prev - 1))}
            disabled={index === 0}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              index === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            <ChevronLeft size={14} />
            Précédente
          </button>

          <div className="flex items-center gap-1.5">
            {questions.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`w-7 h-7 rounded-lg text-xs font-bold transition ${
                  i === index
                    ? 'bg-indigo-500 text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setIndex(prev => Math.min(total - 1, prev + 1))}
            disabled={index === total - 1}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              index === total - 1 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Suivante
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Contenu question */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Énoncé */}
          <div>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
              Question {index + 1}/{total}
            </span>
            {bq?.image && !bq?.texte?.replace(/<[^>]*>/g, '').replace(/\{\{element:\d+\}\}/g, '').trim() && (
              <img src={bq.image} alt="" className="max-w-full max-h-48 rounded-lg mb-3 mx-auto" />
            )}
            <RichTextBlockEleve texte={bq?.texte || ''} elements={elements} />
          </div>

          {/* Réponses */}
          {choix.length > 0 && (
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Réponses</span>
              <div className={`grid gap-2 ${hasElement ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {choix.map((c, i) => {
                  const isCorrect = i === bonneIndex;
                  return (
                    <div
                      key={i}
                      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                        isCorrect
                          ? 'border-emerald-500/50 bg-emerald-500/10'
                          : 'border-slate-600 bg-slate-700/50'
                      }`}
                    >
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold flex-shrink-0 mt-0.5 ${
                        isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-600 text-slate-300'
                      }`}>
                        {isCorrect ? <Check size={14} strokeWidth={3} /> : LETTRES[i]}
                      </span>
                      <div className="flex-1 min-w-0">
                        <ChoixRenderer
                          choix={c}
                          className={`text-sm ${isCorrect ? 'text-emerald-200 font-medium' : 'text-slate-200'}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SessionPreviewModal;
